/* eslint-disable react-hooks/rules-of-hooks */
/**
 * Copyright 2024 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 *
 * 工具函数集合
 *
 * iPhone底部home键适配说明：
 * - getNeedIPhoneHomeIndicatorAdaptation(): 实时检测函数，可以动态获取当前状态
 * - 只有在Flutter应用环境且iPhone X及以后机型时才返回true
 *
 * 使用示例：
 * import { getNeedIPhoneHomeIndicatorAdaptation } from 'src/utils'
 *
 * // 使用实时检测函数（推荐用于动态逻辑）
 * const needAdaptation = getNeedIPhoneHomeIndicatorAdaptation()
 *
 * // 在CSS中的应用示例：
 * // .bottom-content {
 * //   padding-bottom: ${needAdaptation ? 'env(safe-area-inset-bottom)' : '0'};
 * // }
 */

import i18n from "src/i18n";
import QRCode, { QRCodeOptions } from "qrcode";
import { REACT_APP_IMAGE_BASE_URL } from "../env";
import { api } from "src/services/api.service";
import Toast from "src/services/toast.service";
import { UAParser } from 'ua-parser-js';
import { showImagePreview } from "./imagePreview";
import VideoViewer from 'src/components/VideoPreview';
import { NavigateFunction } from "react-router-dom";
import { t } from "i18next";
import { ActStatusEnum, IPathObject } from "src/pages/interfaces";

export const fixPath = (url: string) => {
  // 使用正则表达式替换连续的斜杠
  return url.replace(/\/{2,}/g, "/");
};

/**
 * 截断昵称显示
 * @param nickname 用户昵称
 * @param maxLength 最大显示字符数，默认6
 * @returns 截断后的昵称，超过长度时显示前5个字+省略号
 */
export const truncateNickname = (nickname?: string, maxLength: number = 6): string => {
  if (!nickname) return "";
  if (nickname.length <= maxLength) return nickname;
  return nickname.slice(0, maxLength - 1) + "...";
};

// 获取图片域名
const IMAGE_BASE_URL = REACT_APP_IMAGE_BASE_URL;
type MediaType = "SVG" | "GIF" | "PNG";
type TypeRegEx = Record<`is${MediaType}`, RegExp>;

const checkMediaType = (url: string, type: MediaType): boolean => {
  const typeRegEx: TypeRegEx = {
    isSVG: /\.svg/i,
    isGIF: /\.gif/i,
    isPNG: /\.png/i,
  };

  return typeRegEx[`is${type}` as keyof TypeRegEx].test(url);
}

export const isVideo = (url: string): boolean => {
  const videoRegEx = /\.(mp4|mov|wmv|flv|avi|mkv|webm|m3u8)(\?.*)?$/i;
  return videoRegEx.test(url);
};

export const isAudio = (url: string): boolean => {
  const audioRegEx = /\.(wav|mp3|m4a|aac|ogg|flac)(\?.*)?$/i;
  return audioRegEx.test(url);
};

export const addTimestamp = (url: string) => {
  return url.includes("?")
    ? `${url}&_t=${Date.now()}`
    : `${url}?_t=${Date.now()}`
}

export const imgUrl = (
  url: string,
  isSquare = true, // 需要裁切为正方形的传true
  isZoom: boolean = false,
  w: number = 360,
  h: number = 360,
  options: { skipFlutterImageBaseUrl?: boolean } = {},
): string => {
  const { skipFlutterImageBaseUrl = false } = options;
  if (!url) return "";
  if (url.indexOf("://") !== -1) {
    return url;
  }
  if (url.indexOf(";base64,") !== -1) {
    return url;
  }
  if (url[0] !== "/") {
    url = "/" + url;
  }
  url = fixPath(url);
  const baseUrl = IMAGE_BASE_URL;
  const imgUrl = `${baseUrl}${url}`
  if (checkMediaType(url, "SVG") || isVideo(url) || isAudio(url)) { return imgUrl }
  // 为防止用户乱改后缀，只要不是svg和gif，全部转成jpg
  let formatPart = ""
  if (!checkMediaType(url, "SVG") && !checkMediaType(url, "GIF") && !checkMediaType(url, "PNG")) {
    formatPart = "/format,jpg/interlace,1"
  }
  const centerClip = "?x-oss-process=image/resize,m_fill,limit_0,w_" + w + ",h_" + h
  const noFill = "?x-oss-process=image/resize,m_lfit,limit_0,w_" + w + ",h_" + h
  return `${baseUrl}${url}${isSquare ? centerClip + formatPart : isZoom ? noFill + formatPart : ""}`;
};

export const videoPlayUrl = (url: string, isSnapshot: boolean = true, time: number = 1000, width: number = 360): string => {
  if (!url) { return "" }
  if (url.indexOf("://") !== -1) {
    url = fixPath(url)
    return url
  }
  if (!isVideoFormat(url)) { return "" }
  if (url[0] !== "/") { url = "/" + url }
  url = fixPath(url)
  return `${IMAGE_BASE_URL}${url}` + (
    isSnapshot ? "?x-oss-process=video/snapshot,t_" + time + ",f_jpg,w_" + width + ",ar_auto" : ""
  )
}

export const i18nChangeLanguage = async (languageCode: string) => {
  // const lng = languageCode.split("-")[0];
  // 先加载语言包，确保翻译资源就绪后再切换语言
  await i18n.loadLanguage(languageCode);
  await i18n.changeLanguage(languageCode);
}

export const formatDateTime = (dateStr: string): string => {
  const now = new Date();
  const inputDate = new Date(dateStr);
  // 获取各部分数据
  const year = inputDate.getFullYear();
  const month = (inputDate.getMonth() + 1).toString().padStart(2, "0");
  const day = inputDate.getDate().toString().padStart(2, "0");
  const hours = inputDate.getHours().toString().padStart(2, "0");
  const minutes = inputDate.getMinutes().toString().padStart(2, "0");
  // 判断是否当天
  const isToday =
    inputDate.getDate() === now.getDate() &&
    inputDate.getMonth() === now.getMonth() &&
    inputDate.getFullYear() === now.getFullYear();
  
  // 判断是否当年
  const isCurrentYear = inputDate.getFullYear() === now.getFullYear();
  if (isToday) {
    return `${hours}:${minutes}`; // 当天 → "14:48"
  } else if (isCurrentYear) {
    return `${month}/${day} ${hours}:${minutes}`; // 当年 → "03/26 14:48"
  } else {
    const shortYear = year.toString().slice(2); // 取后两位年份 → "25"
    return `${shortYear}/${month}/${day} ${hours}:${minutes}`; // 非当年 → "25/03/26 14:48"
  }
}

export const copyText = async(text: string, options?: { successTip?: string, errorTip?: string }): Promise<void> => {
  const {
    successTip = i18n.t("复制成功"),
    errorTip = i18n.t("复制失败"),
  } = options ?? {}
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      Toast.show({ content: successTip, icon: "success" });
      return;
    }
  } catch (e) {
    console.warn("Clipboard API 失败，尝试使用 textarea 回退方案:", e);
  }
  
  const textarea = document.createElement("textarea");
  textarea.value = text;
  
  // 使用 absolute 定位避免 iOS 闪烁问题
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  textarea.style.top = "-9999px";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  textarea.style.userSelect = "none";
  textarea.style.zIndex = "-1";
  
  // 设置最小尺寸确保文本可以被选中
  textarea.style.width = "1px";
  textarea.style.height = "1px";
  textarea.style.minWidth = "1px";
  textarea.style.minHeight = "1px";
  
  document.body.appendChild(textarea);
  
  try {
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    const success = document.execCommand("copy");
    
    if (success) {
      Toast.show({
        content: successTip,
        icon: "success"
      });
    } else {
      Toast.show({
        content: errorTip,
        icon: "fail"
      });
    }
  } catch (error) {
    console.error("复制失败:", error);
    Toast.show({
      content: errorTip,
    });
  } finally {
    // 确保元素被移除
    if (document.body.contains(textarea)) {
      document.body.removeChild(textarea);
    }
  }
}

/**
 * 翻转body
 * 根据当前是否旋转90度，来翻转body
 */
export const bodyOrientation = (isResetDirection = false) => {
  const body = document.body
  const winW = window.innerWidth
  const winH = window.innerHeight
  const isRote90 = document.body.style.transform === "rotate(90deg)"
  if (isResetDirection) {
    body.style.cssText = "";
    // if (isRote90) {
    //   body.style.transform = "rotate(0deg)"; // 旋转 90 度
    //   body.style.width = "100%";
    //   body.style.height = "100%";
    //   body.style.top = 0 + 'px';
    //   body.style.left = 0 + 'px';
    // }
    return;
  }
  // 旋转的角度
  console.log("isRote", isRote90)
  if (isRote90) {
    body.style.cssText = "";
    // body.style.transform = "rotate(0deg)";
    // body.style.width = "100%";
    // body.style.height = "100%";
    // body.style.top = 0 + 'px';
    // body.style.left = 0 + 'px';
  } else {
    body.style.width = `${winH}px`;
    body.style.height = `${winW}px`;
    body.style.top = (winH - winW) / 2 + "px";
    body.style.left = 0 - (winH - winW) / 2 + "px";
    body.style.transform = "rotate(90deg)"; // 旋转 90 度
    body.style.overflow = "hidden";
  }
}

/**
 * 生成链接二维码
 * @param url 生成链接二维码的url
 * @returns 返回二维码的base64图片地址
 */
export const generateQRCodeDataURL = (url: string, options?: QRCodeOptions): Promise<string> => {
  const { version, errorCorrectionLevel = "H" } = options ?? {}
  return new Promise((resolve, reject) => {
      QRCode.toDataURL(url, {
        ...(version ? { version } : {}),
        errorCorrectionLevel,
        margin: 2,                 // 增加边距
        width: 1000,                // 明确指定宽度
        color: {
          dark: "#000000ff",         // 纯黑
          light: "#ffffffff"         // 纯白背景
        },
        rendererOpts: {
          quality: 1,
        },
        scale: 30                   // 高清缩放
      }, (error, dataURL) => {
          if (error) {
            console.error(error)
            reject(error)
          } else {
            resolve(dataURL)
          }
      });
  });
}

/**
 * 保存图片(通常是canvas生成的图片)
 * @param email
 * @returns
 */
export const saveCanvasImg = async (canvasImg?: string, imgName = "save-img.png") => {
  if (!canvasImg) {
    return;
  }
  // 将 Data URL 转换为 Blob
  fetch(canvasImg)
  .then(response => response.blob())
  .then(blob => {
    const file = new File([blob], imgName, { type: "image/png" })
      // 检查是否支持 Web Share API
    if (navigator.share) {
      // 使用 Web Share API 分享图片
      navigator.share({
          title: "save image",
          files: [file],
      })
      .then(() => {
        console.log("分享成功")
        Toast.show({
          content: i18n.t("save-success"),
          maskStyle: { zIndex: 3000 },
        });
      })
      .catch((error) => console.log("分享失败", error));
    } else {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = imgName; // 设置下载的文件名
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      Toast.show({
        content: i18n.t("save-success"),
        maskStyle: { zIndex: 3000 },
      });
    }
  })
  .catch(error => {
    console.error("保存图片失败", error);
    Toast.show({
      content: i18n.t("save-fail"),
      maskStyle: { zIndex: 3000 },
    });
  });
}

export const imagePreview = async (images: string[], defaultIndex: number = 0) => {
  if (!images || images.length === 0) return;
  showImagePreview({ images, defaultIndex })
}

export const videoPreview = async (videoUrl: string) => {
  if (!videoUrl) return;
  // videoUrl = "https://img0.shangshi360.com/lingolink/immtest/hls264-2/master.m3u8"
  VideoViewer.show(videoUrl)
}

// 更新视频字幕
export const videoSetSubtitle = async (subtitleUrl: string) => {
  if (!isInFlutterApp()) {
    VideoViewer.updateSubtitle(subtitleUrl)
  }
}

// 验证邮箱
export const isEmailValid = (email: string) => {
  if (!(email && email.trim())) {
    return false;
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return false;
  }
  return true;
};

// 检测是否在Line内置浏览器中
export const getIsInLineBrowser = (): boolean => {
  return /line/i.test(navigator.userAgent);
};

// 检测是否在微信浏览器中
export const getIsInWechatBrowser = (): boolean => {
  return /micromessenger/i.test(navigator.userAgent);
};

// 检测是否在QQ浏览器或QQ相关环境中
export const getIsInQQBrowser = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  return /qq\/|mqqbrowser|qqbrowser|qq\s\d+/i.test(userAgent);
};

// 检测是否为移动设备
export const isMobileDevice = (): boolean => {
  return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// 检测是否为平板设备
export const isTabletDevice = (): boolean => {
  return /iPad|Android/i.test(navigator.userAgent) && window.screen.width >= 768;
};

// 检测是否为桌面设备（PC、Mac等）
export const isDesktopDevice = (): boolean => {
  return !isMobileDevice() && !isTabletDevice();
};

// 检测是否为Android设备
export const getIsAndroid = (): boolean => {
  return /Android/i.test(navigator.userAgent);
};

// 检测是否支持Line分享（仅移动端和平板，且非微信/QQ环境）
export const isSupportLineShare = (): boolean => {
  // 桌面端不支持Line分享
  if (isDesktopDevice()) {
    return false;
  }
  // 微信浏览器或QQ相关环境不支持Line分享
  if (getIsInWechatBrowser() || getIsInQQBrowser()) {
    return false;
  }
  // 移动端或平板端支持
  return isMobileDevice() || isTabletDevice();
};

// 检测是否支持WhatsApp分享（仅移动端和平板，且非微信/QQ环境）
export const isSupportWhatsAppShare = (): boolean => {
  // 桌面端可以支持WhatsApp Web，但微信/QQ环境不支持
  if (getIsInWechatBrowser() || getIsInQQBrowser()) {
    return false;
  }
  return true;
};

// 检查是否支持Web Share API
export const isSupportWebShare = () => {
  return "canShare" in navigator && typeof navigator.canShare === "function"
    && "share" in navigator && typeof navigator.share === "function";
};

// 检测是否为iOS设备
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
};

// 检测是否为Apple设备（包括iOS和macOS）
export const isAppleDevice = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  navigator.platform === "MacIntel" ||
  /Mac/.test(navigator.platform) ||
  /Mac OS X/.test(navigator.userAgent);
};

// 是否小程序环境
export const isInWechatMiniProgram = async (): Promise<boolean> => {
  // 1. 如果不是浏览器环境（如 SSR），直接返回 false
  if (typeof window === "undefined") {
    return false;
  }
  // 2. 非微信环境，直接返回 false
  if (typeof wx === "undefined" || !wx.miniProgram) {
    return false;
  }

  // 3. 如果是微信环境，尝试用 getEnv 检测
  if (wx.miniProgram.getEnv) {
    try {
      return await new Promise<boolean>((resolve) => {
        const timer = setTimeout(() => {
          console.warn("微信环境检测超时，默认返回 false");
          resolve(false);
        }, 1000); // 1秒超时

        wx.miniProgram.getEnv((res) => {
          clearTimeout(timer);
          resolve(!!res.miniprogram);
        });
      });
    } catch (err) {
      console.warn("wx.miniProgram.getEnv 检测失败:", err);
      return false;
    }
  }

  return false;
};

// 获取url中的wxcode参数
export const getWxCodeFromUrl = (): string | null => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("wxcode") ?? null;
  } catch (e) {
    console.error("解析URL参数失败:", e);
    return null;
  }
};

// 清理localStorage
export const localStorageClear = (isClearAll?: boolean) => {
  if (isClearAll) {
    // 保留 feature-tag / email
    const featureTag = localStorage.getItem("feature-tag");
    const email = localStorage.getItem("email");
    localStorage.clear();
    if (featureTag) {
      localStorage.setItem("feature-tag", featureTag);
    }
    if (email) {
      localStorage.setItem("email", email);
    }
    return;
  }
  localStorage.removeItem("token");
  localStorage.removeItem("uid");
}

/**
 * 跳转到登录页
 * @param isMiniProgram 是否是小程序环境，已在上下文中判断，可传入参数；不传重新请求isInWechatMiniProgram判断
 */
export const goToLogin = async (isMiniProgram?: boolean, customRedirectPath?: string) => {
  
  // const isCurrentMiniProgram = isMiniProgram === undefined
  //   ? await isInWechatMiniProgram()
  //   : isMiniProgram
  localStorageClear(true);
  const path = new URL(window.location.href).toString();
  const originPath = new URL(getShareOrigin()).toString()
  const redirectPath = encodeURIComponent(customRedirectPath || path);
  // if (isCurrentMiniProgram) {
  //   wx.miniProgram.reLaunch({
  //     url: `/pages/index/index?webViewUrl=${originPath}login?redirectPath=${redirectPath}`
  //   });
  // } else {
  //   window.location.replace(`/login?redirectPath=${redirectPath}`);
  // }
  window.location.replace(`/emailLogin?redirectPath=${redirectPath}`);
}

/**
 * 验证昵称是否符合要求 2-40字符
 * @param nickname 昵称
 * @returns 是否符合要求
 */
export const validateNickname = (nickname: string): boolean => {
  return /^.{2,40}$/.test(nickname);
}

/**
 * 获取倒计时
 * @param expireTime 过期时间
 * @returns 倒计时秒数
 */
export const getCountDownSeconds = (expireTime: string | Date | number): number => {
  const now = new Date();
  const expire = new Date(expireTime);
  const remainingSeconds = Math.max(0, Math.floor((expire.getTime() - now.getTime()) / 1000));
  return remainingSeconds
}

/**
 * 处理时间字符串以兼容 iOS
 * @param timeStr 时间字符串
 * @returns 处理后的时间字符串
 */
export const iosTimeStr = (timeStr: string): string => {
  if (timeStr.includes("T")) { return timeStr }
  return timeStr.replace(/-/g, "/")
}

/**
 * 解析时间字符串并转换为本地时区的Date对象
 * @param timeString 时间字符串，可能包含GMT信息或纯yyyy-MM-dd HH:mm:ss格式
 * @returns 转换为本地时区的Date对象
 */
export const parseBeijingTimeToLocal = (timeString: string): Date => {
  const processedTimeString = iosTimeStr(timeString);
  // 检查是否包含GMT信息
  if (processedTimeString.includes('GMT')) {
    // 如果包含GMT信息，直接使用Date构造函数解析
    return new Date(processedTimeString);
  } else {
    // 如果没有GMT信息，当作北京时间处理 (UTC+8)
    // 北京时间是 GMT+0800
    return new Date(processedTimeString + ' GMT+0800');
  }
};

/**
 *
 * @param timeStr 时间字符串
 * @returns 格式化后的时间字符串
 * 今天：显示时分
 * 昨天：显示"昨天"
 * 一周内：显示星期几
 * 同年：显示月日
 * 不同年：显示年月日
 */
export const formatTime = (timeStr?: string): string => {
  if (!timeStr) return '';
  try {
    // 将北京时间字符串转换为时间戳
    // timeStr 格式: "2025-06-10 15:01:07"
    const timestamp = parseBeijingTimeToLocal(timeStr).getTime();
    // 如果时间戳无效，返回空字符串
    if (isNaN(timestamp)) return '';
    const now = Date.now();
    const diff = now - timestamp;
    const oneDay = 24 * 60 * 60 * 1000;
    const oneWeek = 7 * oneDay;
    // 今天：显示时分
    if (diff < oneDay && new Date(timestamp).toDateString() === new Date(now).toDateString()) {
      return new Date(timestamp).toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    }
    // 昨天：显示"昨天"
    const yesterday = new Date(now - oneDay);
    if (new Date(timestamp).toDateString() === yesterday.toDateString()) {
      return i18n.t('yesterday');

    }
    // 一周内：显示星期几
    if (diff < oneWeek) {
      const weekdays = [i18n.t('sunday'), i18n.t('monday'), i18n.t('tuesday'), i18n.t('wednesday'), i18n.t('thursday'), i18n.t('friday'), i18n.t('saturday')]
      return weekdays[new Date(timestamp).getDay()];
    }
    // 更早：显示月日
    const currentYear = new Date().getFullYear();
    const messageYear = new Date(timestamp).getFullYear();
    if (currentYear === messageYear) {
      // 同年：显示月日
      return new Date(timestamp).toLocaleDateString('zh-CN', {
        month: 'numeric',
        day: 'numeric'
      });
    } else {
      // 不同年：显示年月日
      return new Date(timestamp).toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      });
    }
  } catch (error) {
    console.error('时间格式化错误:', error);
    return '';
  }
};

/**
 * 格式化消息时间显示
 * @param createTime 消息创建时间字符串
 * @param options 可选的配置选项
 * @returns 格式化后的时间字符串
 */
export const formatMessageTime = (createTime: string): string => {

  const now = new Date();
  const messageTime = parseBeijingTimeToLocal(createTime);
  
  // 获取今天的开始时间 (00:00:00)
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  // 获取昨天的开始时间 (00:00:00)
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  
  // 获取消息时间的日期部分 (00:00:00)
  const messageDate = new Date(messageTime.getFullYear(), messageTime.getMonth(), messageTime.getDate());
  
  // 格式化时分
  const timeFormat = messageTime.toLocaleTimeString('zh-CN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  
  // 判断时间关系
  if (messageDate.getTime() === today.getTime()) {
    // 今天
    return timeFormat;
  } else if (messageDate.getTime() === yesterday.getTime()) {
    // 昨天
    return `${i18n.t('yesterday')} ${timeFormat}`;
  } else if (messageTime.getFullYear() === now.getFullYear()) {
    // 同年
    const monthDay = messageTime.toLocaleDateString('zh-CN', {
      month: '2-digit',
      day: '2-digit'
    });
    return `${monthDay} ${timeFormat}`;
  } else {
    // 其他情况
    const fullDate = messageTime.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    return `${fullDate} ${timeFormat}`;
  }
}

/**
 * 检测是否为需要适配底部home键的iPhone机型
 * iPhone X及以后的机型都需要适配底部安全区域
 * @returns 是否为需要适配的iPhone机型
 */
export const isIPhoneWithHomeIndicator = (): boolean => {
  // 首先检测是否为iOS设备
  if (!isIOS()) {
    return false;
  }

  // 检测是否为iPhone（排除iPad）
  if (!/iPhone/.test(navigator.userAgent)) {
    return false;
  }

  // 获取屏幕尺寸信息
  const screenHeight = window.screen.height;
  const screenWidth = window.screen.width;
  const devicePixelRatio = window.devicePixelRatio || 1;
  
  // 计算逻辑像素尺寸
  const logicalHeight = screenHeight / devicePixelRatio;
  const logicalWidth = screenWidth / devicePixelRatio;
  
  // iPhone X系列及以后机型的逻辑像素尺寸特征
  // iPhone X/XS/11 Pro/12 mini/13 mini: 375×812
  // iPhone XR/XS Max/11/11 Pro Max/12/12 Pro/13/13 Pro: 414×896
  // iPhone 12 Pro Max/13 Pro Max/14 Plus/15 Plus: 428×926
  // iPhone 14 Pro/15 Pro: 393×852
  // iPhone 14 Pro Max/15 Pro Max: 430×932
  const iPhoneXSeriesScreenSizes = [
    { width: 375, height: 812 }, // iPhone X/XS/11 Pro/12 mini/13 mini
    { width: 414, height: 896 }, // iPhone XR/XS Max/11/11 Pro Max/12/12 Pro/13/13 Pro
    { width: 428, height: 926 }, // iPhone 12 Pro Max/13 Pro Max/14 Plus/15 Plus
    { width: 393, height: 852 }, // iPhone 14 Pro/15 Pro
    { width: 430, height: 932 }, // iPhone 14 Pro Max/15 Pro Max
    { width: 390, height: 844 }, // iPhone 12/13/14
  ];

  // 检查当前设备尺寸是否匹配iPhone X系列尺寸（考虑横竖屏）
  const matchesSize = iPhoneXSeriesScreenSizes.some(size =>
    (logicalWidth === size.width && logicalHeight === size.height) ||
    (logicalWidth === size.height && logicalHeight === size.width)
  );

  if (matchesSize) {
    return true;
  }

  // 备用检测方法：检测是否支持安全区域
  // 如果支持 CSS 的 safe-area-inset-bottom，说明是需要适配的iPhone机型
  if (CSS && CSS.supports && CSS.supports("padding-bottom", "env(safe-area-inset-bottom)")) {
    return true;
  }

  // 最后的兜底检测：通过 matchMedia 检测安全区域
  try {
    const safeAreaQuery = "(display-mode: standalone) and (max-device-width: 430px) and (-webkit-device-pixel-ratio: 3)";
    if (window.matchMedia && window.matchMedia(safeAreaQuery).matches) {
      return true;
    }
  } catch {
    // 忽略matchMedia错误
  }

  return false;
};

/**
 * 检测是否在Flutter应用中运行
 * @returns 是否在Flutter应用中
 */
export const isInFlutterApp = (): boolean => {
  const isBlabla = navigator.userAgent.includes("blabla");
  // console.log("isBlabla", isBlabla)
  return isBlabla
};

/**
 * 获取是否需要适配iPhone底部home键的状态
 * 这是一个函数形式，可以实时检测状态变化
 * @returns 是否需要适配iPhone底部home键
 */
export const getNeedIPhoneHomeIndicatorAdaptation = (): boolean => {
  // TEST: XCR
  // return true
  return isInFlutterApp();
};

/**
 * 检测是否为iOS手机并且有刘海屏或灵动岛
 * 包括iPhone X及以后的所有机型
 * @returns 是否为有刘海屏或灵动岛的iOS手机
 */
export const isIPhoneWithNotchOrDynamicIsland = (): boolean => {
  // 首先检测是否为iOS设备
  if (!isIOS()) {
    return false;
  }

  // 检测是否为iPhone（排除iPad）
  if (!/iPhone/.test(navigator.userAgent)) {
    return false;
  }

  // 获取屏幕尺寸信息
  const screenHeight = window.screen.height;
  const screenWidth = window.screen.width;
  const devicePixelRatio = window.devicePixelRatio || 1;
  
  // 计算逻辑像素尺寸
  const logicalHeight = screenHeight / devicePixelRatio;
  const logicalWidth = screenWidth / devicePixelRatio;
  
  // iPhone X及以后机型的逻辑像素尺寸特征（有刘海屏或灵动岛）
  // iPhone X/XS/11 Pro/12 mini/13 mini: 375×812
  // iPhone XR/XS Max/11/11 Pro Max/12/12 Pro/13/13 Pro: 414×896
  // iPhone 12 Pro Max/13 Pro Max/14 Plus/15 Plus: 428×926
  // iPhone 14 Pro/15 Pro: 393×852
  // iPhone 14 Pro Max/15 Pro Max: 430×932
  // iPhone 15/15 Plus: 393×852 / 430×932
  // iPhone 16/16 Plus: 393×852 / 430×932
  const iPhoneWithNotchScreenSizes = [
    { width: 375, height: 812 }, // iPhone X/XS/11 Pro/12 mini/13 mini
    { width: 414, height: 896 }, // iPhone XR/XS Max/11/11 Pro Max/12/12 Pro/13/13 Pro
    { width: 428, height: 926 }, // iPhone 12 Pro Max/13 Pro Max/14 Plus/15 Plus
    { width: 393, height: 852 }, // iPhone 14 Pro/15 Pro/15/16
    { width: 430, height: 932 }, // iPhone 14 Pro Max/15 Pro Max/15 Plus/16 Plus
    { width: 390, height: 844 }, // iPhone 12/13/14
  ];

  // 检查当前设备尺寸是否匹配有刘海屏的iPhone尺寸（考虑横竖屏）
  const matchesSize = iPhoneWithNotchScreenSizes.some(size =>
    (logicalWidth === size.width && logicalHeight === size.height) ||
    (logicalWidth === size.height && logicalHeight === size.width)
  );

  if (matchesSize) {
    return true;
  }

  // 备用检测方法：检测是否支持安全区域
  // 如果支持 CSS 的 safe-area-inset-top，说明是有刘海屏的iPhone
  if (CSS && CSS.supports && CSS.supports("padding-top", "env(safe-area-inset-top)")) {
    return true;
  }

  // 检测是否支持动态岛相关的CSS环境变量
  if (CSS && CSS.supports && CSS.supports("padding-top", "env(safe-area-inset-top)")) {
    // 动态岛设备通常支持更多的安全区域变量
    return true;
  }

  // 最后的兜底检测：通过 matchMedia 检测高分辨率设备
  try {
    const highResQuery = "(max-device-width: 430px) and (-webkit-device-pixel-ratio: 3)";
    if (window.matchMedia && window.matchMedia(highResQuery).matches) {
      return true;
    }
  } catch {
    // 忽略matchMedia错误
  }

  return false;
};

/**
 * "李晓丽"、"xxx"、"xxx"和"Lily"加入了群聊
 * @param names: string[]
 * @returns string
 */
export const getJoinGroupMsg = (names: string[]) => {
  if (!names?.length || names.length === 0) return ""
  names = names.map(name => `"${name}"`)
  if (names.length === 1) {
    return i18n.t("groupJoin.single", { name: names[0] }) //`${names[0]}加入了群聊`;
  } else if (names.length === 2) {
    return i18n.t("groupJoin.dual", { name1: names[0], name2: names[1] }) //`${names[0]}和${names[1]}加入了群聊`;
  } else {
    return i18n.t("groupJoin.multiple", { nameList: names.slice(0, -1).join("、"), lastName: names[names.length - 1] }) //`${names.slice(0, -1).join('、')}和${names[names.length - 1]}加入了群聊`;
  }
}

/**
 * "用户A"、"用户B"、"用户C"和"用户D"被移出了群聊
 * @param names: string[]
 * @returns string
 */
export const getMoveOutGroupMsg = (names: string[]) => {
  if (!names?.length || names.length === 0) return ""
  names = names.map(name => `"${name}"`)
  if (names.length === 1) {
    return i18n.t("moveOutGroup.single", { name: names[0] })
  } else {
    return i18n.t("moveOutGroup.multiple", { nameList: names.slice(0, -1).join("、"), lastName: names[names.length - 1] })
  }
}

/**
 * 从 URL 中移除指定的查询参数
 * @param url - 原始 URL
 * @param paramsToRemove - 要移除的参数名数组
 * @returns 移除指定参数后的 URL
 */
export const removeUrlParams = (url: string, paramsToRemove: string[]): string => {
  try {
    const urlObj = new URL(url);
    paramsToRemove.forEach(param => {
      urlObj.searchParams.delete(param);
    });
    return urlObj.toString();
  } catch (error) {
    console.warn('removeUrlParams: Invalid URL provided', url);
    return url;
  }
};

/**
 * 判断文件是否为视频格式
 * @param fileName 文件名
 * @returns boolean 是否为视频格式
 */
export const isVideoFormat = (fileName: string): boolean => {
  if (!fileName) return false;
  
  // 视频格式正则表达式
  // (?:pattern) 表示非捕获组
  // \. 转义点号
  // $ 表示结尾
  // i 表示不区分大小写
  const videoRegex = /\.((?:mp4|mov|avi|wmv|flv|f4v|mkv|webm|mpeg|mpg|m4v|3gp|3g2|rm|rmvb|vob|ts|m2ts|mts|asf|ogv|mxf|divx|m2v))$/i;
  
  return videoRegex.test(fileName);
};

/**
 * 将生日数据转换为Date对象
 * 支持时间戳（包括负数）、YYYY-MM-DD格式和其他常见格式
 * @param birthday 生日数据（时间戳字符串、日期字符串等）
 * @returns Date对象，无效时返回null
 */
export const parseBirthdayToDate = (birthday: string): Date | null => {
  if (!birthday) return null;
  
  try {
    let date: Date;
    
    // 如果是数字（包括负数），当作时间戳处理
    if (/^-?\d+$/.test(birthday)) {
      const timestamp = parseInt(birthday);
      
      // 验证时间戳范围：1800年到2100年之间
      const minTimestamp = new Date(1800, 0, 1).getTime(); // 约-5364662400000
      const maxTimestamp = new Date(2100, 11, 31).getTime(); // 约4102444800000
      
      if (timestamp < minTimestamp || timestamp > maxTimestamp) {
        console.warn('时间戳超出合理范围:', timestamp);
        return null;
      }
      
      date = new Date(timestamp);
    } else {
      // 使用现有的iOS兼容方法处理日期字符串
      const processedTimeString = iosTimeStr(birthday);
      date = new Date(processedTimeString);
    }
    
    // 验证日期有效性
    if (isNaN(date.getTime())) return null;
    
    // 额外验证：确保日期在合理范围内（1800-2100年）
    const year = date.getFullYear();
    if (year < 1800 || year > 2100) {
      console.warn('日期年份超出合理范围:', year);
      return null;
    }
    
    return date;
  } catch (error) {
    console.error('生日解析错误:', error);
    return null;
  }
};

/**
 * 格式化生日显示
 * @param birthday 生日数据（时间戳字符串、日期字符串等）
 * @returns 格式化后的生日字符串（YYYY-MM-DD），无效时返回null
 */
export const formatBirthdayDisplay = (birthday: string): string | null => {
  const date = parseBirthdayToDate(birthday);
  if (!date) return null;
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * 将Date对象转换为生日提交格式（YYYY-MM-DD）
 * @param date Date对象
 * @returns YYYY-MM-DD格式的字符串
 */
export const formatBirthdayForSubmit = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

// 获取当前设备是否为移动设备
export function checkIsMobile(): boolean {
  if (typeof window === 'undefined') return false;
  const parser = new UAParser(window.navigator.userAgent);
  const deviceType = parser.getDevice().type;
  const isMobileDevice = deviceType === 'mobile' || deviceType === 'tablet'
  const platform = window.navigator?.platform?.toLowerCase?.() ?? '';
  const isPcPlatform = /win|mac/.test(platform);
  return isMobileDevice && !isPcPlatform;
  
  // return deviceType === 'mobile' || deviceType === 'tablet';
  // 真机移动设备：mobile / tablet
  /*  const isMobileDevice = deviceType === 'mobile' || deviceType === 'tablet';
    
    // 排除 PC 平台（Win / Mac）
    const platform = window.navigator?.platform?.toLowerCase?.() ?? '';
    const isPcPlatform = /win|mac|linux/.test(platform);
    
    return isMobileDevice*/
}

// 将Markdown转换为文本
export const markdownToText = (markdown: string = ""): string => {
  return markdown
    // 移除代码块标记
    .replace(/```[a-z]*\n/g, '')
    .replace(/```/g, '')
    // 移除标题标记
    .replace(/#{1,6}\s?/g, '')
    // 移除粗体标记
    .replace(/\*\*(.*?)\*\*/g, '$1')
    // 移除斜体标记
    .replace(/\*(.*?)\*/g, '$1')
    // 移除链接
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    // 移除图片
    .replace(/!\[(.*?)\]\(.*?\)/g, '$1')
    // 移除引用标记
    .replace(/^\s*>\s+/gm, '')
    // 移除无序列表标记
    .replace(/^\s*[-*+]\s+/gm, '')
    // 移除有序列表标记
    .replace(/^\s*\d+\.\s+/gm, '')
    // 移除行内代码标记
    .replace(/`([^`]+)`/g, '$1')
    // 移除HTML标签
    .replace(/<[^>]+>/g, '')
    // 移除多余的空行
    .replace(/\n\s*\n\s*\n/g, '\n\n')
    // 换行用空格" "替换
    .replace(/\n/g, " ")
}

export function isMarkdownText(test: string): boolean {
  if (!test || test.trim().length === 0) return false;
  // 简单检测是否包含常见的Markdown语法特征
  const markdownPatterns = [
    /(^|\s)# .+/m,               // 标题
    /(^|\s)## .+/m,
    /(^|\s)### .+/m,
    /\*\*.+\*\*/m,               // 粗体
    /\*.+\*/m,                   // 斜体
    /`.+`/m,                     // 行内代码
    /```[\s\S]+?```/m,           // 代码块
    /\[.+\]\(.+\)/m,             // 链接
    /!\[.*\]\(.+\)/m,            // 图片
    /^> .+/m,                    // 引用
    /^- .+/m,                    // 无序列表
    // /^\d+\. .+/m                 // 有序列表
  ];
  
  return markdownPatterns.some(pattern => pattern.test(test));
}

// 获取当前设备是否为移动设备
export function getShareOrigin(): string {
  return window.location.origin;
}

export function getDomain(): string {
  try {
    const url = new URL(getShareOrigin());
    return url.hostname;
  } catch (error) {
    console.error('获取域名失败:', error);
    return '';
  }
}

// 打开链接，Flutter环境使用Flutter方法，其他环境使用新标签页打开
export const openUrl = (url: string, type?: any): void => {
  if (!url) return;
  const hiddenLink = document.createElement("a");
  hiddenLink.href = url;
  hiddenLink.target = "_blank";
  document.body.appendChild(hiddenLink);
  hiddenLink.click();
  document.body.removeChild(hiddenLink);
}

// Line分享
export const lineShare = (text: string): void => {
  const lineShareLink = `https://line.me/R/share?text=${encodeURIComponent(text)}`;
  openUrl(lineShareLink);
}

// WhatsApp分享
export const whatsappShare = (text: string) => {
  const whatsappShareLink = `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
  openUrl(whatsappShareLink);
}

  // 扫码
 export const scanQrcode = async (navigate: NavigateFunction) => {
  // 处理非URL文本
  const analyzeText = (text: string) => {
    const screenShareRegex = /^【(.+)】.+：(.+)_(.+) 【.+】$/;
    const matchScreenShare = text.match(screenShareRegex);
    if (matchScreenShare) {
      const [, , roomId] = matchScreenShare;
      navigate(`/meetingV3?roomId=${roomId}&autoScreenShare=true`)
    }
  }
  const SCAN_CODE_DOMAIN = ["chat.blablalink.live", "rtcproject.qunjielong.com", "blabla.qunyuyue.com"]
  navigate("/scanCodePage");
};
/**
 * 将URL路径转换为路径对象
 * @param urlPath 完整的URL路径（如 '/chat?roomId=xxx&contactType=0'）
 * @returns 转换后的路径对象
 */
export const urlToPathObject = (urlPath: string): IPathObject => {
  try {
    // 补全URL以便解析
    const fullUrl = new URL(urlPath, getShareOrigin());
    
    // 提取路径名并转换为router
    const pathname = fullUrl.pathname;
    const router = pathname === '/' ? '' : pathname.substring(1);
    
    // 创建路径对象
    const pathObj: IPathObject = { router };
    
    // 处理查询参数
    fullUrl.searchParams.forEach((value, key) => {
      pathObj[key] = value;
    });
    
    return pathObj;
  } catch (error) {
    console.error('Invalid URL path:', urlPath);
    return { router: '' };
  }
}

/**
 * 比较两个路径对象是否相等
 * @param obj1 第一个路径对象
 * @param obj2 第二个路径对象
 * @returns 是否相等
 */
export const comparePathObjects = (obj1: IPathObject, obj2: IPathObject): boolean => {
  // 获取所有键并排序（确保顺序不影响比较）
  const keys1 = Object.keys(obj1).sort();
  const keys2 = Object.keys(obj2).sort();
  
  // 比较键的数量
  if (keys1.length !== keys2.length) {
    return false;
  }
  
  // 比较每个键值对
  for (let i = 0; i < keys1.length; i++) {
    const key = keys1[i];
    if (key !== keys2[i]) {
      return false;
    }
    
    // 将值转换为字符串进行比较（处理数字和字符串差异）
    const val1 = String(obj1[key]);
    const val2 = String(obj2[key]);
    
    if (val1 !== val2) {
      return false;
    }
  }
  
  return true;
}

/**
 * 判断当前路径是否与路径对象匹配
 * @param currentPath 当前URL路径
 * @param pathObj 目标路径对象
 * @returns 是否匹配
 */
export const isPathMatch = (currentPath: string, pathObj: IPathObject): boolean => {
  const currentPathObj = urlToPathObject(currentPath);
  return comparePathObjects(currentPathObj, pathObj);
}

export const getFileIcon = (fileName: string): string => {
  const iconMap: Record<string, string> = {
    'pdf': 'file02.png',
    'doc': 'file03.png',
    'docx': 'file03.png',
    'xls': 'file04.png',
    'xlsx': 'file04.png',
    'ppt': 'file05.png',
    'pptx': 'file05.png',
    'txt': 'file06.png',
    'default': 'file07.png',
  }
  return iconMap[fileName?.split('.').pop()?.toLowerCase() || 'default'] || 'file07.png';
}

/**
 * 判断活动状态
 * @param startTime 活动开始时间，可以是string | Date | number格式
 * @param endTime 活动结束时间，可以是string | Date | number格式
 * @returns 对应的ActStatusEnum值
 */
export const getActivityStatus = (startTime: string | Date | number, endTime: string | Date | number): ActStatusEnum => {
  const now = new Date().getTime();
  
  // 统一转换为时间戳
  const startTimestamp = new Date(startTime).getTime();
  const endTimestamp = new Date(endTime).getTime();
  
  // 检查时间有效性
  if (isNaN(startTimestamp) || isNaN(endTimestamp)) {
    throw new Error('Invalid startTime or endTime');
  }
  
  if (now < startTimestamp) {
    return ActStatusEnum.PENDING; // 未开始
  } else if (now >= startTimestamp && now <= endTimestamp) {
    return ActStatusEnum.STARTED; // 进行中
  } else {
    return ActStatusEnum.ENDED; // 已结束
  }
}

/**
 * 获取价格区间
 * @param minPrice
 * @param maxPrice
 * @returns 价格区间字符串
 */
export const getPriceRange = (minPrice: number = 0, maxPrice: number = 0): string => {
  if (minPrice === maxPrice) {
    return (minPrice / 100).toFixed(2)
  } else {
    return `${(minPrice / 100).toFixed(2)} ~ ${(maxPrice / 100).toFixed(2)}`
  }
}

/**
 * 获取图片数组
 * @param pics 字符串,逗号分隔
 * @returns 图片数组
 */
export const getPicList = (pics: string): string[] => {
  if (!pics) return [];
  return pics.split(',').map(item => item.trim());
}

/**
 * 获取用户匿名名称
 * 保留第一个字符，加上两个*
 */
export const getAnonymousName = (name: string): string => {
  return name?.substring(0, 1) + '**' || '';
}
